import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowRight, UserRound } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, IMAGES } from "../../data/content";
import { useAuth } from "../../context/AuthContext";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "py-2.5" : "py-5"}`}
      data-testid="site-header"
    >
      {/* Dynamic African backdrop — becomes vivid on scroll */}
      <div className={`absolute inset-0 overflow-hidden transition-opacity duration-700 ${scrolled ? "opacity-100" : "opacity-0"}`} aria-hidden="true">
        <div className="absolute inset-0 bg-navy/90 backdrop-blur-xl" />
        <img src={IMAGES.heroAbidjan} alt="" className="absolute inset-0 h-[400%] w-full object-cover object-center opacity-25 -translate-y-1/3" />
        <div className="absolute inset-0 bg-gradient-to-r from-night via-navy/70 to-night" />
        {/* animated gold sheen */}
        <motion.div
          className="absolute -inset-x-1/2 top-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent"
          animate={{ x: ["-30%", "130%"] }}
          transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
        />
        <div className="absolute bottom-0 inset-x-0 h-px bg-gold/25" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 flex items-center justify-between gap-4">
        <Logo size={scrolled ? "h-9" : "h-11"} />

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={`relative text-sm font-body tracking-wide transition-colors duration-300 group ${
                pathname === item.to ? "text-gold" : "text-white/85 hover:text-white"
              }`}
            >
              {item.label}
              <span className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-all duration-300 ${pathname === item.to ? "w-full" : "w-0 group-hover:w-full"}`} />
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <Link to="/pays" className="text-xs font-body text-white/60 hover:text-gold transition-colors" data-testid="header-discover-countries">
            🇫🇷 · 🇧🇯 · 🇨🇮 Découvrez les pays
          </Link>
          <Link
            to={user ? "/espace-client" : "/connexion"}
            data-testid="header-client-space"
            className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 px-4 py-2 text-sm font-medium text-gold hover:bg-gold/10 transition-colors"
          >
            <UserRound size={15} /> {user ? "Mon espace" : "Espace client"}
          </Link>
          <button
            onClick={() => navigate("/contact")}
            data-testid="header-contact-button"
            className="group inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-gold to-gold-light px-5 py-2.5 text-sm font-semibold text-navy transition-transform duration-300 hover:-translate-y-0.5"
          >
            Contacter
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>

        <button className="lg:hidden text-white p-2" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Ouvrir le menu">
          <Menu size={26} />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-night lg:hidden flex flex-col"
            data-testid="mobile-menu-overlay"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-gold/15">
              <Logo size="h-9" />
              <button onClick={() => setOpen(false)} className="text-white p-2" data-testid="mobile-menu-close" aria-label="Fermer le menu"><X size={26} /></button>
            </div>
            <nav className="flex-1 flex flex-col justify-center gap-1 px-8">
              {NAV.concat([{ label: "Découvrez les pays", to: "/pays" }, { label: user ? "Mon espace" : "Espace client", to: user ? "/espace-client" : "/connexion" }]).map((item, i) => (
                <motion.div key={item.to} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.07 * i }}>
                  <Link to={item.to} className="font-display text-3xl text-white hover:text-gold transition-colors py-2 block" data-testid={`mobile-nav-${item.to.replace("/", "")}`}>{item.label}</Link>
                </motion.div>
              ))}
            </nav>
            <div className="p-8 border-t border-gold/15">
              <p className="text-white/60 text-sm mb-4">🇫🇷 France · 🇧🇯 Bénin · 🇨🇮 Côte d'Ivoire</p>
              <Link to="/contact" className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-gold to-gold-light px-6 py-4 font-semibold text-navy w-full" data-testid="mobile-contact-button">Contacter <ArrowRight size={18} /></Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
